import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { StorageItem } from '@vsolv/packages/storage/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Claim } from '@wsphere/warranties/domain';
import { BehaviorSubject } from 'rxjs';
import { CLAIM_ITEM_CONTROLS_CONFIG } from '../../config/claim-item-controls.config';
import {
  ClaimItemHoldCompletion,
  ClaimItemHoldEvidenceMode,
  ClaimItemHoldEvidenceOptions,
} from '../../dialogs/claim-item-hold-complete/claim-item-hold-complete.dialog';
import { ClaimItemNotePrompt } from '../../dialogs/claim-item-note/claim-item-note.dialog';
import { ClaimItemHoldService, ClaimService } from '../../services';

type PendingClose = { kind: 'cancelHold'; id: string } | { kind: 'resolveEscalation'; id: string };

type EvidencePrompt = { open(options?: ClaimItemHoldEvidenceOptions): void };

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-holds',
  templateUrl: './claim-item-holds.component.html',
  standalone: false,
})
export class ClaimItemHoldsComponent {
  constructor(
    private holdSvc: ClaimItemHoldService,
    private claimSvc: ClaimService,
    private toastSvc: ToastService
  ) {}

  readonly config = CLAIM_ITEM_CONTROLS_CONFIG;
  readonly open = Claim.ClaimItem.Hold.Status.OPEN;

  @Input() set claim(claim: Claim.Model | null) {
    this._claim = claim;
    this.refresh();
  }
  get claim() {
    return this._claim;
  }
  private _claim: Claim.Model | null = null;

  @Input() set item(item: Claim.ClaimItem.Model | null) {
    this._item = item;
    this.refresh();
  }
  get item() {
    return this._item;
  }
  private _item: Claim.ClaimItem.Model | null = null;

  /** For showing which charge a hold is about. */
  @Input() lines: Claim.ClaimItem.Line.Model[] = [];

  /** Only hides the add buttons; the server checks too. */
  @Input() canManage = false;

  /** Closing existing holds and escalations still works once the job is resolved. */
  @Input() canClose = false;

  /** Lets the complete dialog show the file picker; the server hides linked files too. */
  @Input() canViewFiles = false;

  /** Lets the complete dialog upload new files to the claim; the server checks too. */
  @Input() canUploadFiles = false;

  /** Claim Editors and up can link files to open holds; the server checks too. */
  @Input() canAttachEvidence = false;

  /** Holds changed, so the charges table can update its hold badges. */
  @Output() changed = new EventEmitter<void>();

  readonly holds$ = new BehaviorSubject<Claim.ClaimItem.Hold.Model[]>([]);
  readonly escalations$ = new BehaviorSubject<Claim.ClaimItem.Escalation.Model[]>([]);

  /** Claim files by id, for the evidence thumbnails. */
  filesById = new Map<string, StorageItem.Model>();

  private pending: PendingClose | null = null;
  private evidenceFor: { holdId: string; mode: ClaimItemHoldEvidenceMode } | null = null;

  /** Linking files needs the picker, so it needs attachment access too. */
  get canAttach() {
    return this.canAttachEvidence && this.canViewFiles;
  }

  async refresh() {
    if (!this._claim || !this._item) return;

    const [holds, escalations] = await Promise.all([
      this.holdSvc.holds(this._claim.id, this._item.id).catch(() => null),
      this.holdSvc.escalations(this._claim.id, this._item.id).catch(() => null),
    ]);

    this.holds$.next(holds?.items ?? []);
    this.escalations$.next(escalations?.items ?? []);
    await this.loadEvidenceFiles(this._claim.id, holds?.items ?? []);
  }

  /** Evidence only stores file ids, so the thumbnails need the claim's files. */
  private async loadEvidenceFiles(claimId: string, holds: Claim.ClaimItem.Hold.Model[]) {
    const hasEvidence = holds.some(hold => hold.evidence?.some(file => !file.removed));
    if (!this.canViewFiles || !hasEvidence) return;

    const files = await this.claimSvc.listAllDocuments(claimId).catch(() => []);
    this.filesById = new Map(files.map(file => [file.id, file]));
  }

  async reload() {
    await this.refresh();
    this.changed.emit();
  }

  chargeName(hold: Claim.ClaimItem.Hold.Model) {
    if (!hold.claimItemLineId) return this.config.holds.wholeJob;
    return this.lines.find(line => line.id === hold.claimItemLineId)?.description ?? this.config.holds.removedCharge;
  }

  /** Due all day on the due date; overdue from the next day. */
  isOverdue(hold: Claim.ClaimItem.Hold.Model) {
    const endOfDueDay = new Date(hold.dueDate);
    endOfDueDay.setHours(24, 0, 0, 0);
    return hold.status === this.open && endOfDueDay.getTime() <= Date.now();
  }

  statusTheme(status: Claim.ClaimItem.Hold.Status) {
    return status === this.open ? 'warn' : status === Claim.ClaimItem.Hold.Status.COMPLETED ? 'success' : 'default';
  }

  askToComplete(completeDialog: EvidencePrompt, hold: Claim.ClaimItem.Hold.Model) {
    this.askForEvidence(completeDialog, hold, 'complete');
  }

  askToAttach(completeDialog: EvidencePrompt, hold: Claim.ClaimItem.Hold.Model) {
    this.askForEvidence(completeDialog, hold, 'attach');
  }

  private askForEvidence(dialog: EvidencePrompt, hold: Claim.ClaimItem.Hold.Model, mode: ClaimItemHoldEvidenceMode) {
    this.evidenceFor = { holdId: hold.id, mode };
    dialog.open({ mode, linked: hold.evidence ?? [] });
  }

  async confirmEvidence({ note, documentIds }: ClaimItemHoldCompletion) {
    const pending = this.evidenceFor;
    this.evidenceFor = null;
    if (!pending || !this._claim || !this._item) return;

    const { id: claimId } = this._claim;
    const { id: itemId } = this._item;

    if (pending.mode === 'attach') {
      await this.send(
        this.holdSvc.attachEvidence(claimId, itemId, pending.holdId, documentIds),
        this.config.holds.attached
      );
    } else {
      await this.send(this.holdSvc.complete(claimId, itemId, pending.holdId, note, documentIds));
    }
  }

  /** Opens a linked file; the dialog fetches a fresh link. */
  openEvidence(
    documentDialog: { open(props: { claim: Claim.Model; document: { id: string } }): void },
    file: Claim.ClaimItem.Hold.EvidenceFile
  ) {
    if (!this._claim || file.removed) return;
    documentDialog.open({ claim: this._claim, document: { id: file.storageItemId } });
  }

  askToClose(noteDialog: { open(prompt: ClaimItemNotePrompt): void }, pending: PendingClose) {
    this.pending = pending;
    noteDialog.open(this.config.noteDialog[pending.kind]);
  }

  async confirmClose(note: string | null) {
    const pending = this.pending;
    this.pending = null;
    if (!pending || !this._claim || !this._item) return;

    const { id: claimId } = this._claim;
    const { id: itemId } = this._item;

    await this.send(
      pending.kind === 'cancelHold'
        ? this.holdSvc.cancel(claimId, itemId, pending.id, note ?? '')
        : this.holdSvc.resolveEscalation(claimId, itemId, pending.id, note)
    );
  }

  private async send(request: Promise<unknown>, success?: { title: string; text: string }) {
    const result = await request.catch((response: HttpErrorResponse) => {
      const message = response?.error?.message;
      this.toastSvc.show({
        type: 'error',
        title: this.config.noteDialog.errorTitle,
        text: Array.isArray(message) ? message.join(' ') : message,
      });
      return null;
    });

    if (!result) return;
    if (success) this.toastSvc.show({ type: 'success', ...success });
    await this.reload();
  }
}
